<template>
  <section class="fb card-custom">
    <!-- ---------- Pestañas de periodo ---------- -->
    <div class="fb-tabs">
      <button
        class="fb-tab"
        :class="{ on: filtros.modo === 'comparativo' }"
        @click="set({ modo: 'comparativo', anio: null })"
      >
        Comparativo
      </button>
      <button
        v-for="a in aniosDisponibles"
        :key="a"
        class="fb-tab"
        :class="{ on: filtros.modo === 'anio' && Number(filtros.anio) === a }"
        @click="set({ modo: 'anio', anio: a })"
      >
        {{ a }}
      </button>
    </div>

    <!-- ---------- Años a comparar (selección múltiple) ---------- -->
    <div v-if="filtros.modo === 'comparativo'" class="fb-years">
      <span class="fb-years-label">Años a comparar</span>
      <button
        v-for="a in aniosDisponibles"
        :key="'cmp' + a"
        class="fb-year"
        :class="{ on: activo(a), lock: activo(a) && aniosActivos.length <= 2 }"
        :aria-pressed="activo(a)"
        :title="
          activo(a) && aniosActivos.length <= 2
            ? 'Se necesitan al menos dos años para comparar'
            : activo(a)
              ? `Quitar ${a} de la comparación`
              : `Agregar ${a} a la comparación`
        "
        @click="toggleAnio(a)"
      >
        <i class="bi" :class="activo(a) ? 'bi-check-square-fill' : 'bi-square'"></i>
        {{ a }}
      </button>
      <span class="fb-years-hint">Comparando: {{ aniosActivos.join(" · ") }}</span>
    </div>

    <!-- ---------- Selectores ---------- -->
    <div class="fb-selects">
      <label class="fb-field">
        <span class="sr-only">Cuenta</span>
        <select
          :value="filtros.idCuenta || ''"
          @change="set({ idCuenta: $event.target.value || null })"
        >
          <option value="">Cuenta: Todas</option>
          <option v-for="c in cuentas" :key="idDe(c)" :value="idDe(c)">
            {{ nombreDe(c) }}
          </option>
        </select>
      </label>

      <label class="fb-field">
        <span class="sr-only">Área</span>
        <select
          :value="filtros.area || ''"
          @change="set({ area: $event.target.value || null })"
        >
          <option value="">Área: Todas</option>
          <option v-for="s in secciones" :key="s" :value="s">Área: {{ s }}</option>
        </select>
      </label>

      <label class="fb-field">
        <span class="sr-only">Estado del cliente</span>
        <select
          :value="filtros.estado"
          @change="set({ estado: $event.target.value })"
        >
          <option value="todos">Estado: Todos</option>
          <option value="VERDE">Estado: Favorable</option>
          <option value="AMARILLO">Estado: En seguimiento</option>
          <option value="ROJO">Estado: Crítico</option>
        </select>
      </label>

      <label class="fb-field">
        <span class="sr-only">Evaluación</span>
        <select
          :value="filtros.evaluacion"
          @change="set({ evaluacion: $event.target.value })"
        >
          <option value="todas">Evaluación: Todas</option>
          <option value="evaluadas">Evaluación: Evaluadas</option>
          <option value="pendientes">Evaluación: Pendientes</option>
        </select>
      </label>
    </div>

    <!-- ---------- Chips activos + resultados ---------- -->
    <div class="fb-bottom">
      <div class="fb-chips">
        <span v-if="!chips.length" class="fb-nochips">Sin filtros activos</span>
        <button
          v-for="c in chips"
          :key="c.key"
          class="fb-chip"
          @click="set(c.limpiar)"
        >
          {{ c.label }} <i class="bi bi-x"></i>
        </button>
        <button v-if="chips.length" class="fb-clear" @click="limpiarTodo">
          Limpiar todo
        </button>
      </div>

      <span class="fb-count">
        {{ resultado.cuentas }} cuentas · {{ resultado.encuestas }} encuestas
      </span>
    </div>
  </section>
</template>

<script>
export default {
  name: "ExecutiveFilterBar",

  props: {
    filtros: { type: Object, required: true },
    aniosDisponibles: { type: Array, default: () => [] },
    cuentas: { type: Array, default: () => [] },
    secciones: { type: Array, default: () => [] },
    // { cuentas, encuestas } — conteo tras aplicar los filtros
    resultado: { type: Object, default: () => ({ cuentas: 0, encuestas: 0 }) },
  },

  emits: ["update"],

  computed: {
    // Años realmente en juego. Selección vacía = todos los disponibles.
    aniosActivos() {
      const elegidos = (this.filtros.aniosComparar || [])
        .map(Number)
        .filter((a) => this.aniosDisponibles.includes(a))
        .sort((a, b) => a - b);
      return elegidos.length ? elegidos : this.aniosDisponibles.slice();
    },

    // Un chip por filtro activo, cada uno con el parche que lo desactiva.
    chips() {
      const lista = [];

      if (this.filtros.modo === "anio" && this.filtros.anio) {
        lista.push({
          key: "anio",
          label: `Año ${this.filtros.anio}`,
          limpiar: { modo: "comparativo", anio: null },
        });
      }
      if (
        this.filtros.modo === "comparativo" &&
        this.aniosActivos.length < this.aniosDisponibles.length
      ) {
        lista.push({
          key: "anios",
          label: this.aniosActivos.join(" vs "),
          limpiar: { aniosComparar: [] },
        });
      }
      if (this.filtros.idCuenta) {
        const c = this.cuentas.find(
          (x) => String(this.idDe(x)) === String(this.filtros.idCuenta),
        );
        lista.push({
          key: "cuenta",
          label: c ? this.nombreDe(c) : `Cuenta ${this.filtros.idCuenta}`,
          limpiar: { idCuenta: null },
        });
      }
      if (this.filtros.area) {
        lista.push({
          key: "area",
          label: this.filtros.area,
          limpiar: { area: null },
        });
      }
      if (this.filtros.estado && this.filtros.estado !== "todos") {
        const etiquetas = {
          VERDE: "Favorable",
          AMARILLO: "En seguimiento",
          ROJO: "Crítico",
        };
        lista.push({
          key: "estado",
          label: etiquetas[this.filtros.estado] || this.filtros.estado,
          limpiar: { estado: "todos" },
        });
      }
      if (this.filtros.evaluacion && this.filtros.evaluacion !== "todas") {
        lista.push({
          key: "evaluacion",
          label:
            this.filtros.evaluacion === "evaluadas" ? "Evaluadas" : "Pendientes",
          limpiar: { evaluacion: "todas" },
        });
      }

      return lista;
    },
  },

  methods: {
    idDe(c) {
      return c.idcuenta ?? c.idCuenta;
    },

    nombreDe(c) {
      return c.nombre ?? c.NOMBRE ?? "—";
    },

    activo(a) {
      return this.aniosActivos.includes(a);
    },

    // Alterna un año de la comparación. Se exigen al menos dos: quitar el
    // penúltimo dejaría una vista de un solo año, que ya tiene su pestaña.
    toggleAnio(a) {
      const actuales = this.aniosActivos;
      const esta = actuales.includes(a);
      if (esta && actuales.length <= 2) return;

      const nuevos = esta
        ? actuales.filter((x) => x !== a)
        : [...actuales, a].sort((x, y) => x - y);

      this.$emit("update", {
        modo: "comparativo",
        anio: null,
        // Seleccionar todos equivale a no filtrar.
        aniosComparar:
          nuevos.length === this.aniosDisponibles.length ? [] : nuevos,
      });
    },

    set(parche) {
      this.$emit("update", parche);
    },

    limpiarTodo() {
      this.$emit("update", {
        modo: "comparativo",
        anio: null,
        aniosComparar: [],
        idCuenta: null,
        area: null,
        estado: "todos",
        evaluacion: "todas",
      });
    },
  },
};
</script>

<style scoped>
.fb {
  border-radius: 16px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: sticky;
  top: 8px;
  z-index: 4;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* ---------- Pestañas ---------- */
.fb-tabs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.fb-tab {
  border: 1px solid var(--border);
  background: transparent;
  color: var(--txt-dim);
  border-radius: 999px;
  padding: 6px 16px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
}
.fb-tab:hover {
  border-color: var(--primary);
  color: var(--primary);
}
.fb-tab.on {
  background: var(--primary);
  border-color: var(--primary);
  color: var(--on-accent);
}

/* ---------- Años a comparar ---------- */
.fb-years {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 10px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
}

.fb-years-label {
  font-size: 10px;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--txt-faint);
}

.fb-year {
  border: 1px solid var(--border);
  background: var(--white);
  color: var(--txt-dim);
  border-radius: 8px;
  padding: 4px 11px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.18s ease;
}
.fb-year:hover {
  border-color: var(--primary);
}
.fb-year.on {
  border-color: var(--primary);
  color: var(--brand-text);
  background: var(--surface-2);
}
/* Último par activo: no se puede desmarcar, se necesitan dos para comparar. */
.fb-year.lock {
  cursor: not-allowed;
  opacity: 0.75;
}

.fb-years-hint {
  margin-left: auto;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--txt-dim);
  font-variant-numeric: tabular-nums;
}

/* ---------- Selectores ---------- */
.fb-selects {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
}

.fb-field {
  min-width: 0;
}

.fb-field select {
  width: 100%;
  border: 1px solid var(--border);
  background: var(--white);
  color: var(--txt);
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 12.5px;
  font-family: inherit;
  cursor: pointer;
}
.fb-field select:focus {
  outline: none;
  border-color: var(--primary);
}

/* ---------- Chips ---------- */
.fb-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  border-top: 1px solid var(--border);
  padding-top: 10px;
}

.fb-chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
}

.fb-nochips {
  font-size: 11.5px;
  color: var(--txt-faint);
}

.fb-chip {
  border: 1px solid var(--primary);
  background: var(--surface);
  color: var(--brand-text);
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.fb-chip:hover {
  background: var(--surface-2);
}

.fb-clear {
  border: none;
  background: none;
  color: var(--txt-dim);
  font-size: 11.5px;
  text-decoration: underline;
  cursor: pointer;
  padding: 3px 4px;
}
.fb-clear:hover {
  color: var(--rojo);
}

.fb-count {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--txt-dim);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
</style>
